import { useState, useCallback, useEffect } from 'react';
import { ProductAttributeService } from '../services/ProductAttributeService';
import type { ProductAttribute, ProductAttributeValue } from '../services/ProductAttributeService';

export const useProductAttributes = (productId?: number) => {
  const [attributes, setAttributes] = useState<ProductAttribute[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAttributes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ProductAttributeService.getAttributes(productId);
      setAttributes(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch attributes');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchAttributes();
  }, [fetchAttributes]);

  const createAttribute = async (data: Partial<ProductAttribute>) => {
    const newAttr = await ProductAttributeService.createAttribute(data);
    setAttributes(prev => [...prev, newAttr]);
    return newAttr;
  };

  const updateAttribute = async (id: number, data: Partial<ProductAttribute>) => {
    const updated = await ProductAttributeService.updateAttribute(id, data);
    setAttributes(prev => prev.map(a => a.id === id ? updated : a));
    return updated;
  };

  const deleteAttribute = async (id: number) => {
    await ProductAttributeService.deleteAttribute(id);
    setAttributes(prev => prev.filter(a => a.id !== id));
  };

  const addValue = async (attributeId: number, data: Partial<ProductAttributeValue>) => {
    const newVal = await ProductAttributeService.addValue(attributeId, data);
    setAttributes(prev => prev.map(a => {
      if (a.id === attributeId) {
        return { ...a, values: [...(a.values || []), newVal] };
      }
      return a;
    }));
    return newVal;
  };

  const removeValue = async (attributeId: number, valueId: number) => {
    await ProductAttributeService.removeValue(attributeId, valueId);
    setAttributes(prev => prev.map(a => {
      if (a.id === attributeId) {
        return { ...a, values: (a.values || []).filter(v => v.id !== valueId) };
      }
      return a;
    }));
  };

  return {
    attributes,
    loading,
    error,
    refetch: fetchAttributes,
    createAttribute,
    updateAttribute,
    deleteAttribute,
    addValue,
    removeValue
  };
};
